import { useContent } from "contexts/contentContext/contentContext";
import { EMAIL_VALIDATOR } from "helpers/validators";
import Form from "components/Reusable/Form/Form";
import { useUser } from "contexts/userContext/userContext";
import {
  type Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { BillingDataT } from "components/Pages/Purchases_new/models/models";
import {
  CurrencyE,
  PaymentTypeE,
  PurchaseStepE,
} from "components/Pages/Purchases_new/models/enums";
import useCreditCard from "components/Pages/Purchases_new/hooks/useCreditCard";

import useCurrentCurrency from "components/Pages/Plans/Utils/useCurrentCurrency";

import styles from "./thanksBillingForm.module.scss";
import s from "components/Pages/Purchases_new/components/BillingInformationSection/components/BillingDataForm/styles.module.scss";
import { useAppDispatch } from "store/hooks";
import { setBillingData } from "components/Pages/Purchases_new/store/purchasesSlice";
import fetchPaymentUrl from "components/Reusable/ThanksActionButton/api/fetchPaymentUrl";
import {
  DEFAULT_COUNTRY_ID,
  TRANSACTION_PURCHASE_TYPE,
} from "components/Reusable/ThanksActionButton/constants/constants";
import { useLocation } from "react-router-dom";

interface ThanksBillingFormI {
  step: PurchaseStepE;
  setStep: Dispatch<SetStateAction<PurchaseStepE>>;
  isSecondStep: boolean;
  setIframeUrl: Dispatch<SetStateAction<string>>;
  transactionAmount: number;
}

const PHONE_VALIDATOR = /^\+\d+$/;

const ThanksBillingForm = memo(
  ({
    step,
    setStep,
    isSecondStep,
    setIframeUrl,
    transactionAmount,
  }: ThanksBillingFormI) => {
    const { getTextByKey } = useContent();
    const location = useLocation();
    const { getUserData } = useUser();
    const { paymentType } = useCreditCard();
    const currency = useCurrentCurrency();
    const currentCurrency = CurrencyE[currency as keyof typeof CurrencyE];

    const [enableSubmit, setEnableSubmit] = useState(false);

    const dispatch = useAppDispatch();

    const pathSegments = location.pathname.split("/").filter(Boolean);
    const lastSegment = pathSegments.pop();
    const secondLastSegment = pathSegments.pop();

    const currentPage =
      lastSegment === "home"
        ? lastSegment
        : `${secondLastSegment}/${lastSegment}`;

    useEffect(() => {
      setEnableSubmit((prev) => !prev);
    }, [step]);

    const fields = {
      firstName: {
        label: "purchase-first-name-label",
        placeholder: "purchase-first-name-label",
        fieldKey: "firstName",
        element: "input",
        type: "text",
        validation: {
          required: true,
          invalidMessage: "default-input-invalid-message",
        },
      },
      lastName: {
        label: "purchase-last-name-label",
        placeholder: "purchase-last-name-label",
        fieldKey: "lastName",
        element: "input",
        type: "text",
        validation: {
          required: true,
          invalidMessage: "default-input-invalid-message",
        },
      },
      email: {
        label: "purchase-email-label",
        fieldKey: "email",
        placeholder: "purchase-email-label",
        element: "input",
        type: "email",
        validation: {
          required: true,
          invalidMessage: "invalid-email-text",
          reg: EMAIL_VALIDATOR,
        },
      },
      phone: {
        label: "purchase-phone-label",
        placeholder: "purchase-phone-label",
        fieldKey: "phone",
        element: "input",
        type: "tel",
        validation: {
          required: true,
          reg: PHONE_VALIDATOR,
          invalidMessage: "invalid-phone-text",
        },
      },
      separator: {
        element: "customelement",
        fieldKey: "separator",
        render: () => (
          <div className={styles.formSeparator}>
            <hr />
            <div className={styles.formSubHeader}>
              <span />
              <h2>{getTextByKey("invoice-settings-title")}</h2>
            </div>
          </div>
        ),
      },
      nameForInvoice: {
        label: "purchase-invoice-name-label",
        placeholder: "purchase-invoice-name-label",
        fieldKey: "nameForInvoice",
        element: "input",
        type: "text",
        validation: {
          required: true,
          invalidMessage: "default-input-invalid-message",
        },
      },
      idForInvoice: {
        label: "purchase-company-id-label",
        placeholder: "purchase-company-id-label",
        fieldKey: "idForInvoice",
        element: "input",
        type: "text",
      },
    };

    const handleSubmit = useCallback(
      async (data: BillingDataT) => {
        const transactionData = {
          TransactionEmail: data?.email?.trim(),
          TransactionFirstName: data?.firstName?.trim(),
          TransactionLastName: data?.lastName?.trim(),
          TransactionPhone: data?.phone?.trim(),
          TransactionCountryId: DEFAULT_COUNTRY_ID,
          TransactionPriceDisplayedToUser: transactionAmount,
          TransactionPurchaseProvider: paymentType,
          TransactionPurchaseType: TRANSACTION_PURCHASE_TYPE,
          TransactionUserIsAuthenticated: true,
          TransactionCurrency: currentCurrency,
          getOnlyIframe: true,
          TransactionPackageName: TRANSACTION_PURCHASE_TYPE,
          TransactionUserPassword: "123123",
          UrlResponse: `${currentPage}/payment-success`,
        };

        dispatch(setBillingData(data));

        const paymentUrl = await fetchPaymentUrl(transactionData);

        if (paymentUrl) {
          if (paymentType === PaymentTypeE.CREDIT_CARD) {
            setStep(PurchaseStepE.THIRD_PAYMENT_STEP);
            setIframeUrl(paymentUrl);
          } else if (paymentType === PaymentTypeE.PAYPAL) {
            window.location.href = paymentUrl;
          }
        }
      },
      [
        currentCurrency,
        currentPage,
        dispatch,
        paymentType,
        setIframeUrl,
        setStep,
        transactionAmount,
      ]
    );

    const onCancel = () => {
      setStep(PurchaseStepE.FIRST_PURCHASE_METHOD_STEP);
    };

    return (
      <div className={`${s.paymentSettingsWrapper} ${styles.formWrap}`}>
        <Form
          // @ts-ignore
          fields={fields}
          onSubmit={handleSubmit}
          submitProps={{
            title: getTextByKey("next"),
          }}
          cancelProps={{
            title: getTextByKey("back-text"),
            className: `${styles.backBtn}`,
          }}
          onCancel={onCancel}
          defaultValuesObj={{
            firstName: getUserData()?.UserFirstName,
            lastName: getUserData()?.UserLastName,
            email: getUserData()?.UserName,
            phone: getUserData()?.UserPhone,
            nameForInvoice:
              getUserData()?.User_Invoice_Name ||
              (getUserData()?.UserFirstName && getUserData()?.UserLastName
                ? getUserData()?.UserFirstName +
                  " " +
                  getUserData()?.UserLastName
                : undefined),
            idForInvoice: getUserData()?.User_Invoice_Id,
          }}
          extenralSuccessCancel={enableSubmit}
        />
      </div>
    );
  }
);

export default ThanksBillingForm;
